import React, { ReactNode, useEffect } from 'react';

import { FeatureToggles } from '@paralleldrive/react-feature-toggles';
import { Amplify } from 'aws-amplify';
import analytics from 'lib/analytics';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Store } from 'redux';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import AppCrashFallback from 'components/shared/AppCrashFallback';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import Internationalization from 'components/shared/Internationlization';
import { IN_DEV_FEATURE_FLAG } from 'constants/featureToggle';
import { showInDevelopmentFeatures } from 'services/helpers/featureToggle';
import { parseUrlParameters } from 'services/parameters/parseUrl';

const USER_PASSWORD_AUTH = 'USER_PASSWORD_AUTH';

Amplify.configure({
  Auth: {
    region: 'eu-west-1',
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_WEBCLIENT_ID,
    authenticationFlowType: USER_PASSWORD_AUTH,
  },
});

interface Props {
  persistor: Persistor;
  store: Store;
  locale: string;
  children: ReactNode;
}

const App: React.FunctionComponent<Props> = ({ persistor, store, locale, children }) => {
  useEffect(() => {
    if (process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID) {
      const tagManagerArgs = {
        gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID,
      };
      TagManager.initialize(tagManagerArgs);
    }
    parseUrlParameters();
    analytics.init('aba6329cfce33be958a6d404c293db73');
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Internationalization {...{ locale }}>
          <ErrorBoundary FallbackComponent={AppCrashFallback}>
            <BrowserRouter>
              <FeatureToggles features={showInDevelopmentFeatures() ? [IN_DEV_FEATURE_FLAG] : []}>
                {/* biome-ignore lint/complexity/noUselessFragments: <explanation> */}
                <>{children}</>
              </FeatureToggles>
            </BrowserRouter>
          </ErrorBoundary>
        </Internationalization>
      </PersistGate>
    </Provider>
  );
};

export default App;
